/**
 * Catalogo de campos de uma entidade: UMA descricao que serve a tabela, aos detalhes e a edicao.
 *
 * Antes eram dois lugares: o formulario tinha a sua lista de campos, e os detalhes despejavam o
 * objeto cru - `CLIENT_ID`, `SECRET_REF`, `IS_ACTIVE` em caixa alta, sem rotulo nem ordem. Isso nao
 * e so feio: e a tela admitindo que nao sabe o que cada campo significa. Com um catalogo so, o
 * rotulo que a pessoa le ao conferir e o mesmo que ela le ao editar, e um campo novo no backend
 * aparece nos dois lugares ou em nenhum.
 */

import type { ReactNode } from 'react'

export interface Opcao {
  valor: string
  rotulo: string
}

/** Como o valor e APRESENTADO. Nao muda o que e enviado, so como se le. */
export type Formato = 'texto' | 'identificador' | 'codigo' | 'booleano' | 'url'

export interface Edicao<T> {
  tipo: 'texto' | 'textarea' | 'booleano' | 'selecao'
  dica?: string
  exemplo?: string
  /** So para `selecao`. Recebe o item para filtrar por cliente, por exemplo. */
  opcoes?: (item: T) => Opcao[]
  /** Texto vazio vira `null` no envio, para campo opcional no backend. */
  opcional?: boolean
}

export interface CampoMeta<T> {
  /** Chave no payload do backend. */
  nome: string
  rotulo: string
  /** Secao do painel. Campos sem grupo caem em "Geral". */
  grupo?: string
  formato?: Formato
  /** Texto curto abaixo do valor, nos detalhes. Explica o que o campo decide. */
  nota?: string
  /** Valor de EXIBICAO quando o bruto nao serve (id de conexao -> nome da conexao). */
  exibir?: (item: T) => ReactNode
  /** Ausente = campo nao editavel. */
  edicao?: Edicao<T>
  /** Fora dos detalhes (ruido interno que nao ajuda ninguem). */
  oculto?: boolean
}

export const GRUPO_PADRAO = 'Geral'

/** Agrupa preservando a ordem em que os grupos aparecem no catalogo. */
export function porGrupo<T>(campos: Array<CampoMeta<T>>): Array<[string, Array<CampoMeta<T>>]> {
  const grupos = new Map<string, Array<CampoMeta<T>>>()
  for (const campo of campos) {
    const chave = campo.grupo ?? GRUPO_PADRAO
    const atual = grupos.get(chave)
    if (atual === undefined) grupos.set(chave, [campo])
    else atual.push(campo)
  }
  return [...grupos.entries()]
}

/**
 * Campos que o backend devolveu e o catalogo nao descreve.
 *
 * Aparecem no fim, em "Outros dados", em vez de sumir: um campo novo no backend fica visivel ate
 * alguem decidir como ele se chama na tela. Esconder seria perder informacao em silencio.
 */
export function naoCatalogados<T>(item: T, campos: Array<CampoMeta<T>>): string[] {
  const conhecidos = new Set(campos.map((campo) => campo.nome))
  return Object.keys(item as Record<string, unknown>).filter((chave) => !conhecidos.has(chave))
}
